import type { InboxFilters } from "@/components/inbox/types"

// Query params for the current folder + filter state. Undefined values are
// dropped by Inertia's router, so "inbox" (the default folder) and empty
// filters produce a clean /inbox URL.
export function inboxFilterParams(filters: InboxFilters) {
  return {
    folder: filters.folder === "inbox" ? undefined : filters.folder,
    status: filters.status ?? undefined,
    client_id: filters.client_id ?? undefined,
    classification: filters.classification ?? undefined,
    lane: filters.lane ?? undefined,
    q: filters.q || undefined,
  }
}

export function csrfToken() {
  return document
    .querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
    ?.content
}

export function formatInboxDate(value: string | null) {
  if (!value) return "Unknown"

  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value))
}

export function smartleadInboxUrl(reply: {
  smartlead_stats_id: string | null
  smartlead_campaign_id: number | null
  smartlead_lead_id: number | null
  smartlead_lead_map_id: number | null
  smartlead_inbox_url: string | null
}): string | null {
  if (reply.smartlead_inbox_url) {
    return reply.smartlead_inbox_url
  }
  if (reply.smartlead_lead_map_id) {
    return `https://app.smartlead.ai/app/master-inbox?action=INBOX&leadMap=${reply.smartlead_lead_map_id}`
  }
  if (reply.smartlead_lead_id) {
    return `https://app.smartlead.ai/app/master-inbox?action=INBOX&leadMap=${reply.smartlead_lead_id}`
  }
  if (reply.smartlead_campaign_id) {
    return `https://app.smartlead.ai/app/email-campaign/${reply.smartlead_campaign_id}/leads`
  }
  return null
}

// Operator-facing names for reply statuses — keep in sync with the folder
// labels in InboxRail.tsx ("sent" reads as "Responded").
const STATUS_LABELS: Record<string, string> = {
  new: "New",
  drafting: "Redrafting",
  awaiting_review: "Review",
  snoozed: "Snoozed",
  sent: "Responded",
  archived: "Archived",
  ignored: "Ignored",
  blocked: "System-blocked",
  won: "Won",
}

export function statusLabel(value: string) {
  return STATUS_LABELS[value] ?? value
}

// Compact lane labels for the inbox list rows ("automation" reads as "Auto").
const LANE_LABELS: Record<string, string> = {
  automation: "Auto",
  leadgen: "Leadgen",
}

export function laneLabel(value: string) {
  return LANE_LABELS[value] ?? value
}

export function classificationLabel(value: string) {
  return value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ")
}
